import { useEffect, useRef, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import BasicModal from './BasicModal';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { CheckIcon, LinkIcon } from '@heroicons/react/24/outline';
import { useDiffitUserContext } from '../../contexts/DiffitUserContext';
import { saveUserSettings } from '../../services/UserSettingsService';
import { redeemCode } from '../../services/PromoCodeService';
import { markUserNuxAsSeen } from '../../services/UserService';
import { Mixpanel } from '../../services/Mixpanel';
import ReferralBonusFootnote from './ReferralBonusFootnote';
import ReferralRewardDetail from './ReferralRewardDetail';
import {
  ClassesSelection,
  GradeSelection,
  InterventionSpecialistTypeSelection,
  RoleSelection,
  SchoolSelection,
  SubjectSelection,
  doPreferToKnowSchool,
  getInferredSubjectsFromClasses,
  getInitialSelections,
  isInterventionSpecialist,
} from './ProfileFields';
import InviteOthersForm from '../InviteOthersForm';
import { MATH_PROMO_CODE, MATH_REFERRAL_PROGRAM_KEY } from '../../constants/mathPromo';
import { SEPTEMBER_SCRAMBLE_PROGRAM_KEY } from '../../constants/septemberScramble';
import { MATH } from '../../constants/userProfile';
import { INVITE_SOURCE, LINK_SOURCE } from '../../constants/linkSources';
import {
  enrollInReferralProgram,
  getMyReferralStatus,
  sendReferralInvites,
} from '../../services/ReferralService';
import { isReferralProgramKeyConfigured } from '../../utils/referralProgramConfig';
import {
  buildInvitePayload,
  hasPayableBonusTier,
  inviteSendTrackingProps,
  inviteStepSubheading,
  isClaimableReward,
  mergeInviteOutcomes,
  splitAddressInput,
  unsentAddresses,
} from '../../utils/inviteOthers';
import { isValidEmail } from '../../utils/email';
import { useReferralCode } from '../../hooks/useShareUrl';
import { useTutorialContext } from '../../routes/Base/Context/TutorialContext';

// The campaign this wizard's invites belong to, mirrored from referral_controller's
// KEY_BTS_2026_ONBOARDING. The backend maps it to a program through the
// referral-program-configuration flag and uses it to pick the invite email's template — it
// does not decide what a referral earns, which still comes from the referrer's assignments.
const INVITE_PROGRAM_KEY = 'bts_2026_onboarding';

const ROLE_STEP = {
  type: 'role',
  title: 'What do you do?',
  subheading: 'Select all that apply',
};

const GRADE_STEP = {
  type: 'grade',
  title: 'What grades are you teaching this year?',
  subheading: 'Select all that apply',
};

const SUBJECT_STEP = {
  type: 'subject',
  title: 'What subjects are you teaching this year?',
  subheading: 'Select all that apply',
};

const CLASSES_STEP = {
  type: 'classes',
  title: 'What classes are you teaching this year?',
  subheading: 'Select all that apply',
};

const INTERVENTION_SPECIALIST_TYPE_STEP = {
  type: 'intervention_specialist_type',
  title: 'Which of these student groups do you support?',
  subheading: 'Select all that apply',
};

const SCHOOL_STEP = {
  type: 'school',
  title: 'Where do you work?',
  subheading: 'Search by school name or district name',
};

const INVITE_STEP = {
  type: 'invite_others',
  title: 'Most teachers hear about Diffit from colleagues',
  // No static subheading: it interpolates the threshold and reward, neither of which is
  // known until GET /referral/me resolves, so it's always set at push time below.
  optional: true,
};

const CLAIM_STEP = {
  type: 'claim_reward',
  title: 'Thank you!',
  subheading: 'We appreciate you spreading the word about Diffit.',
};

// INVITE_STEP's copy in the math promo's words — a replacement, not an extra step, so every
// index, dot and Back click keeps working. The free year is granted on arrival at the step (see
// the arrival effect), so the copy states it as a fact and the screen is only the ask. Not
// `optional`, and no "Not now": with nothing to claim, "Continue to Diffit" is the exit.
const MATH_STEP_COPY = {
  title: 'You have premium access this school year!',
  subheading: 'Math teachers get a free year of Diffit Premium. Share with your colleagues below!',
  optional: false,
};

// The copy once a redemption has been refused: it can no longer state the grant, so the offer
// moves up and only the ask stays below. (For "already redeemed" the grant holds anyway, and
// the refusal above it says so.)
const MATH_STEP_REFUSED_COPY = {
  title: 'Math teachers get a free year of Diffit Premium',
  subheading: 'Share with your colleagues below!',
};

// No retry to offer — the redemption fires once, on arrival.
const GENERIC_CLAIM_ERROR = "We couldn't apply your free year. Please contact us.";

// How long an exit waits on the arrival redemption before closing anyway. Long enough for the
// redeem and its nux write over a slow connection; short enough that a hung request (axios
// allows 90s) can't trap a first-time user, who has no X and no "Not now". Past it, the on-close
// refresh may beat the redeem and the packet banner re-offer the code — the lesser harm.
const CLOSE_WAIT_MS = 5000;

// POST /referral/enroll maps this surface to bts_2026_math. Enrolment is what makes the offer
// deliverable — "no assignment means no rewards" (async_get_assigned_programs), so without it
// the friends this step emails get nothing.
const MATH_ENROLMENT_SURFACE = 'math_promo';

// TODO: implement as an experiment
// const ALL_SET_STEP = {
//   type: 'all_set',
//   title: "You're all Set!",
//   subheading: 'Based on your responses, here are a few ready-to-use resources teachers love',
// };

// INVITE_STEP and CLAIM_STEP are deliberately absent here — whether they're shown at
// all depends on the async referral-program lookup (see inviteProgram below), so
// onClickContinue's newSteps construction is their one source of truth.
const STEPS = [
  ROLE_STEP,
  GRADE_STEP,
  SUBJECT_STEP,
  CLASSES_STEP,
  INTERVENTION_SPECIALIST_TYPE_STEP,
  SCHOOL_STEP,
  // ALL_SET_STEP,
];

const shouldShowRoleNameInSubheading = (roles) => {
  if (roles?.length === 1) {
    return false;
  }
  const isClassroomTeacher = roles.includes('classroom_teacher');
  const isInstructionalCoach = roles.includes('instructional_coach');
  return isClassroomTeacher + isInterventionSpecialist(roles) + isInstructionalCoach > 1;
};

export const OnboardingWizardBTS2026 = ({ showWizard, setShowWizard }) => {
  const { diffitUser, hasActiveSubscription, isLDidentified } = useDiffitUserContext();
  const { referralProgramConfiguration } = useFlags();
  const { showTutorial } = useTutorialContext();
  const referralCode = useReferralCode();
  const { has_completed_onboarding_bts_2026: hasCompletedOnboardingBts2026 } =
    diffitUser.seen_nux_json || {};
  const [selections, setSelections] = useState(() => ({
    ...getInitialSelections(diffitUser),
    // Referral state, deliberately not in the shared getInitialSelections: EditProfileModal
    // uses that too and has no business carrying invite addresses. `invited` holds the
    // committed chips; `inviteDraft` holds text typed but not yet committed, so Continue
    // can submit it too (see the invite_others case below).
    invited: [],
    inviteDraft: '',
  }));
  const [stepOptions, setStepOptions] = useState(STEPS);
  const [currentStep, setCurrentStep] = useState(stepOptions[0]);
  const [isAddSchoolFormShown, setIsAddSchoolFormShown] = useState(false);

  // undefined = still loading; null = this teacher isn't assigned to an "invited"-step
  // program; otherwise the program that gates the invite/claim steps.
  // Unassigned teachers therefore never see the invite ask at all, which is what makes
  // them a usable control group.
  const [inviteProgram, setInviteProgram] = useState(undefined);
  // The sharer-side bonus, over only the footnote — a wider question than inviteProgram below,
  // and answered off the same lookup (see hasPayableBonusTier).
  const [hasBonusProgram, setHasBonusProgram] = useState(false);
  const [bonusFootnote, setBonusFootnote] = useState(undefined);
  const [isInviteSubmitting, setIsInviteSubmitting] = useState(false);
  // Cumulative across submits, keyed by normalized address: a response only covers the
  // batch it was sent, so the last one alone can't label chips from an earlier one.
  const [inviteOutcomes, setInviteOutcomes] = useState({});
  const [inviteProgress, setInviteProgress] = useState(null);
  const [inviteSubmitError, setInviteSubmitError] = useState(null);
  // Sticky, unlike inviteSubmitError (cleared at the start of every send): once a send has
  // failed on the math step, its "Not now" exit stays put through the retry instead of blinking
  // out while the request is in flight.
  const [hasInviteSendFailed, setHasInviteSendFailed] = useState(false);
  const inviteThreshold = inviteProgram?.next_tier?.threshold;
  // Non-null wherever the reward-promise subheading is rendered — the math offer pushes the same
  // step with no tier, and states its own promise instead.
  const inviteNextTier = inviteProgram?.next_tier;

  // Set only by a grant made on this step; read by the tracking below.
  const [hasClaimedFreeYear, setHasClaimedFreeYear] = useState(false);
  const [freeYearError, setFreeYearError] = useState(null);

  // The wizard's own answers, not the classifier — nothing is generated yet. Classroom teachers
  // answer classes rather than subjects, so `subjects` alone would miss an Algebra 1 teacher.
  const isMathTeacher =
    selections.subjects.includes(MATH) ||
    getInferredSubjectsFromClasses(selections.classes).includes(MATH);
  // Redeeming grants a trial, not a subscription, so this still passes after a claim — which is
  // what keeps the ask available to a teacher who just claimed.
  const isFreeOrTrialUser = !!diffitUser && !hasActiveSubscription;
  // The campaign is the gate, not math-promo-banner (that flag is the banner surface's own):
  // pointing bts_2026_math at a program is what makes the referee-side free year deliverable.
  //
  // isLDidentified first, per diffit-react's CLAUDE.md — this wizard can open before LaunchDarkly
  // identifies, and the flag below would then be read against the anonymous context, deciding
  // the offer wrongly and misattributing its impression event. Fails closed.
  const isMathCampaignLive =
    isLDidentified &&
    isReferralProgramKeyConfigured(referralProgramConfiguration, MATH_REFERRAL_PROGRAM_KEY);
  // Deliberately independent of inviteProgram: unassigned teachers are the control group for the
  // generic ask's reward messaging, but this offer is the campaign's own and enrols them itself.
  const isMathFreeYearOffer = isMathTeacher && isFreeOrTrialUser && isMathCampaignLive;
  // seen_nux retires the promo across devices: a packet banner, /redeem or an earlier run already
  // granted it, so there is nothing to redeem here.
  const isFreeYearGranted = !!diffitUser?.seen_nux_json?.math_promo_claimed || hasClaimedFreeYear;

  const hasMounted = useRef(false);
  const initialFocusRef = useRef(null);
  const hasSetUpMathOffer = useRef(false);
  // The arrival redemption, so closing can wait on it: base.jsx refreshes the user the moment
  // the wizard closes, and a refresh that beats the redeem (or the nux write) hands the packet
  // banner a teacher with no trial and no flag — it offers the code again, and that click gets
  // "already redeemed". Made non-rejecting where it is assigned, so the awaits need no catch.
  const freeYearClaim = useRef(Promise.resolve());
  // True from an exit click until the claim above has settled and the wizard closes — the button
  // is disabled and relabelled meanwhile, so a slow network doesn't look like a dead click. The
  // state is for rendering only; the guard is the ref, which a handler mid-await (a send, the
  // remind-later save) still reads fresh where the state in its render closure would be stale.
  const [isClosing, setIsClosing] = useState(false);
  const isClosingRef = useRef(false);
  const hasTrackedMathOffer = useRef(false);

  // Redeems in place, as MathPromoBanner does: /redeem would abandon onboarding for the
  // homepage. Rendering never waits on it — the step is the ask either way, and a refusal only
  // swaps the copy to MATH_STEP_REFUSED_COPY with the error above the field. Finishing does wait
  // (see freeYearClaim).
  const claimFreeYear = async () => {
    Mixpanel.track('Onboarding Wizard - Math Free Year Claim Started');

    let newTrialEndDate;
    try {
      const response = await redeemCode(MATH_PROMO_CODE);
      if (!response.data.success) {
        // A refusal ("already redeemed", "code expired") comes back in a 200 body, and on
        // err.response when the request rejects — unpack both.
        const refusal = new Error(response.data.error || 'Promo redemption failed');
        refusal.reason = response.data.error;
        throw refusal;
      }
      newTrialEndDate = response.data.new_trial_end_date;
    } catch (err) {
      setFreeYearError(err.reason || err.response?.data?.error || GENERIC_CLAIM_ERROR);
      Mixpanel.track('Onboarding Wizard - Math Free Year Claim Error', { error: err.message });
      return;
    }

    setHasClaimedFreeYear(true);
    Mixpanel.track('Onboarding Wizard - Math Free Year Claim Success', {
      new_trial_end_date: newTrialEndDate,
    });

    // After the state above, failure swallowed: the trial is already granted, so a failure here
    // must not surface an error against a redemption that worked. Retires the packet banner. No
    // refreshDiffitUser — re-identifying LaunchDarkly mid-wizard remounts this modal and would
    // drop them back at step one; base.jsx refreshes on close (see freeYearClaim).
    await markUserNuxAsSeen(diffitUser.id, { math_promo_claimed: true }, () => {}).catch(
      () => null
    );
  };

  // Look up the teacher's referral status once, when the wizard opens, to decide
  // whether the invite/claim steps apply at all. A 404 means the feature is
  // disabled for this account — treated the same as "no active program", not an
  // error (see getMyReferralStatus's docs in ReferralService.js).
  useEffect(() => {
    if (!showWizard) return;
    let cancelled = false;
    getMyReferralStatus()
      .then((response) => {
        if (cancelled) return;
        const programs = response?.data?.programs || [];
        const program =
          programs.find((p) => p.qualifying_step === 'invited' && p.next_tier?.threshold) || null;
        setInviteProgram(program);
        setHasBonusProgram(hasPayableBonusTier(programs, SEPTEMBER_SCRAMBLE_PROGRAM_KEY));
        setBonusFootnote(
          programs.find((p) => p.key === SEPTEMBER_SCRAMBLE_PROGRAM_KEY)?.copy?.bonus_footnote
        );
      })
      .catch(() => {
        if (cancelled) return;
        setInviteProgram(null);
        setHasBonusProgram(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showWizard]);

  // On arrival at the step, not on open: math-ness isn't known until they've answered, and
  // enrolling everyone would put non-math teachers in the campaign. Fire-and-forget — a failure
  // is no reason to retract an offer the flags say is live.
  useEffect(() => {
    if (currentStep.type !== 'invite_others' || !isMathFreeYearOffer) return;
    if (!hasTrackedMathOffer.current) {
      hasTrackedMathOffer.current = true;
      Mixpanel.track('Onboarding Wizard - Math Free Year Shown', {
        already_claimed: isFreeYearGranted,
      });
    }
    if (hasSetUpMathOffer.current) return;
    hasSetUpMathOffer.current = true;
    // Tracked, not swallowed: a silent failure here looks identical to a working offer.
    enrollInReferralProgram(MATH_ENROLMENT_SURFACE).catch((err) =>
      Mixpanel.track('Onboarding Wizard - Math Free Year Enrol Failed', { error: err.message })
    );
    // The copy states the year as granted, so the grant can't wait on the button (Back is an
    // exit too); hence on arrival. A refusal is shown, not acted on: the
    // only one reachable with no bug is "already redeemed" (a /redeem before this shipped), and
    // the ask stands either way.
    if (!isFreeYearGranted) freeYearClaim.current = claimFreeYear().catch(() => null);
  }, [currentStep, isMathFreeYearOffer]);

  useEffect(() => {
    const {
      user_completed_onboarding: completedLegacyOnboarding,
      has_completed_onboarding_bts_2025: completedBts2025,
      has_completed_onboarding_bts_2026: hasCompletedOnboardingBts2026,
    } = diffitUser?.seen_nux_json || {};
    // Treat last year's completion as "previously onboarded" so returning users
    // get the Welcome Back + Remind-me-later snooze path.
    const hasCompletedOldOnboarding = completedLegacyOnboarding || completedBts2025 === true;

    if (hasMounted.current) {
      return;
    }

    if (typeof hasCompletedOnboardingBts2026 === 'string') {
      const [type] = hasCompletedOnboardingBts2026.split(':::');
      if (type === 'remind_later') {
        Mixpanel.track('Onboarding Wizard Shown', { type: 'returning_user_reminded' });
      }
    } else if (hasCompletedOldOnboarding && !hasCompletedOnboardingBts2026) {
      Mixpanel.track('Onboarding Wizard Shown', {
        type: 'returning_user_first_time',
      });
    } else if (hasCompletedOldOnboarding || hasCompletedOnboardingBts2026) {
      Mixpanel.track('Onboarding Wizard Shown', {
        type: 'completed',
      });
    } else {
      Mixpanel.track('Onboarding Wizard Shown', {
        type: 'first_time_user',
      });
    }
    hasMounted.current = true;
  }, [diffitUser]);

  // One-shot callout at the sidebar avatar once onboarding finishes, so the
  // teacher knows where their profile and memories can be updated later. The
  // tutorial system silently gives up when the target isn't rendered (mobile
  // hides the sidebar avatar), so no viewport gating is needed here.
  const showAvatarMenuTip = () => {
    Mixpanel.track('Profile Menu Tip Shown');
    showTutorial({
      type: 'Profile Menu Tip',
      // A one-shot callout about the sidebar avatar — it shouldn't trail the
      // teacher onto the next page they open.
      dismissOnNavigate: true,
      steps: [
        {
          targetElementTutorialId: 'profile-avatar-menu',
          header: 'Your profile lives here',
          body: 'Edit your info anytime from Update Profile.',
          alignLeft: true,
        },
      ],
    });
  };

  // Every way out funnels through here (Continue on the last step, "Not now", the X, "Remind me
  // later"), so none of them can let base.jsx's on-close refresh beat the arrival redemption.
  // Usually long settled; a fast exit on a slow network is the case this waits for.
  // Resolves to whether this call closed the wizard, so a caller whose press was the second one
  // skips its own tracking and tip as well.
  const closeWizard = async () => {
    // A second press while waiting (X again, Escape) would close — and refresh — twice.
    if (isClosingRef.current) return false;
    isClosingRef.current = true;
    setIsClosing(true);
    await Promise.race([
      freeYearClaim.current,
      new Promise((resolve) => setTimeout(resolve, CLOSE_WAIT_MS)),
    ]);
    setShowWizard(false);
    return true;
  };

  const isContinueDisabled = () => {
    if (isClosing) return true;
    if (currentStep.type === 'role') {
      return selections.roles.length === 0;
    }
    if (currentStep.type === 'intervention_specialist_type') {
      // if they are an intervention specialist, they need to select at least one intervention specialist type
      return selections.roles.filter((r) => r.startsWith('intervention_specialist_')).length === 0;
    }
    if (currentStep.type === 'grade') {
      return selections.grade_levels.length === 0;
    }
    if (currentStep.type === 'subject') {
      return selections.subjects.length === 0;
    }
    if (currentStep.type === 'classes') {
      return selections.classes.length === 0;
    }
    if (currentStep.type === 'school') {
      return (
        selections.school_records.length === 0 && !selections.does_not_work_for_school_or_district
      );
    }
    if (currentStep.type === 'invite_others') {
      // Enabled even short of the threshold — "Not now" is the exit, Continue is a
      // nudge, not a gate. This only guards against a double-submit.
      return isInviteSubmitting;
    }
    return false;
  };

  const onClickContinue = async () => {
    let inferredSubjects = [];
    let newSteps = stepOptions;
    newSteps = [ROLE_STEP];
    const showRoleNameInSubheading = shouldShowRoleNameInSubheading(selections.roles);
    if (selections.roles.includes('classroom_teacher')) {
      // if they are a classroom teacher, they need to select their grade levels and classes
      newSteps.push(
        {
          ...GRADE_STEP,
          subheading:
            GRADE_STEP.subheading +
            (showRoleNameInSubheading && !isInterventionSpecialist(selections.roles)
              ? ` in your role as a teacher`
              : ''),
        },
        {
          ...CLASSES_STEP,
          subheading:
            CLASSES_STEP.subheading +
            (showRoleNameInSubheading && !isInterventionSpecialist(selections.roles)
              ? ` in your role as a teacher`
              : ''),
        }
      );
    }
    if (isInterventionSpecialist(selections.roles)) {
      // if they are an intervention specialist, they need to select their intervention specialist type and school
      newSteps.push({
        ...INTERVENTION_SPECIALIST_TYPE_STEP,
        subheading:
          INTERVENTION_SPECIALIST_TYPE_STEP.subheading +
          (showRoleNameInSubheading ? ` in your role as an intervention specialist` : ''),
      });
    }
    if (selections.roles.includes('instructional_coach')) {
      // if they are ONLY an instructional coach, they need to select their classes
      newSteps.push({
        ...SUBJECT_STEP,
        subheading:
          SUBJECT_STEP.subheading +
          (showRoleNameInSubheading ? ` in your role as an instructional coach` : ''),
      });
    }

    // all roles need to select their school
    newSteps.push({
      ...SCHOOL_STEP,
      subheading: doPreferToKnowSchool(selections.roles)
        ? "Search for your school's name"
        : 'Search for your school or district name',
    });

    // Shown when the teacher has an active "invited"-step program, or when the math free-year
    // offer applies (which needs no assignment); omitted entirely otherwise. This whole newSteps
    // array is rebuilt from scratch on every click (including a click on claim_reward's own
    // Continue button), so CLAIM_STEP must be re-derived here from persisted state
    // (selections.reward) rather than relying solely on the one-time push inside the
    // 'invite_others' case below — otherwise clicking Continue while already on claim_reward
    // would rebuild newSteps without it and findIndex would come back -1.
    if (inviteProgram || isMathFreeYearOffer) {
      newSteps.push({
        ...INVITE_STEP,
        // The math offer carries its own promise and applies with no tier to interpolate; the
        // generic subheading is built from one, which only exists when inviteProgram does.
        ...(isMathFreeYearOffer
          ? MATH_STEP_COPY
          : {
              subheading: inviteStepSubheading({
                threshold: inviteThreshold,
                rewardType: inviteNextTier.reward_type,
                rewardAmount: inviteNextTier.reward_amount,
              }),
            }),
      });
      if (selections.reward) {
        newSteps.push(CLAIM_STEP);
      }
    }
    setStepOptions(newSteps);

    let currentStepIndex = newSteps.findIndex((step) => step.type === currentStep.type);
    let isFinalStep = currentStepIndex === newSteps.length - 1;
    let staysOnCurrentStep = false;
    switch (currentStep.type) {
      case 'role':
      case 'intervention_specialist_type':
        await saveUserSettings({
          // dedupe, just to be safe
          roles: Array.from(new Set(selections.roles)),
          did_complete_onboarding: isFinalStep,
        });
        break;
      case 'grade':
        await saveUserSettings({
          // dedupe, just to be safe
          grade_levels: Array.from(new Set(selections.grade_levels)),
          did_complete_onboarding: isFinalStep,
        });
        break;
      case 'subject':
        await saveUserSettings({
          // dedupe, just to be safe
          subjects: Array.from(new Set(selections.subjects)),
          did_complete_onboarding: isFinalStep,
        });
        break;
      case 'classes':
        inferredSubjects = getInferredSubjectsFromClasses(selections.classes);
        await saveUserSettings({
          // dedupe, just to be safe
          classes: Array.from(new Set(selections.classes)),
          subjects: Array.from(new Set(inferredSubjects)),
          did_complete_onboarding: isFinalStep,
        });
        break;
      case 'school':
        await saveUserSettings({
          // dedupe, just to be safe
          school_records: Array.from(new Set(selections.school_records)),
          does_not_work_for_school_or_district: selections.does_not_work_for_school_or_district,
          did_complete_onboarding: true,
        });
        break;
      case 'invite_others': {
        setInviteSubmitError(null);
        // Include the uncommitted draft: a teacher who types the last address and clicks
        // Continue without pressing Enter still means to invite them. Split and validated
        // exactly as commit() does — the draft is where commit() *leaves* what it rejected,
        // so folding it in raw would POST the very tokens the field is showing an error for.
        const emails = buildInvitePayload([
          ...selections.invited,
          ...splitAddressInput(selections.inviteDraft).filter(isValidEmail),
        ]);
        // Continue on an untouched step shouldn't POST {emails: []} — nothing to invite, so
        // just move on. "Not now" is the deliberate exit; this is the accidental one.
        if (emails.length === 0) break;
        // Mirror the full list into the chips — including addresses an earlier submit
        // already answered for — so every one has a chip for its label to attach to.
        setSelections((prev) => ({ ...prev, invited: emails, inviteDraft: '' }));
        // Only the addresses the API hasn't answered for. Re-sending an accepted one comes
        // back skipped/already_invited, which flipped its chip to the error tone — and
        // while the shortfall nudge holds the step, every Continue resent the whole list,
        // so a teacher short of the threshold could never get past it.
        const unsent = unsentAddresses(emails, inviteOutcomes);
        if (unsent.length === 0) break;
        setIsInviteSubmitting(true);
        try {
          // The math key so the friends get the free-year template, not the onboarding one. It
          // selects copy only; what a referral earns comes from the referrer's assignments.
          const response = await sendReferralInvites(
            unsent,
            isMathFreeYearOffer ? MATH_REFERRAL_PROGRAM_KEY : INVITE_PROGRAM_KEY,
            INVITE_SOURCE.ONBOARDING_WIZARD
          );
          const rewards = response.data.rewards || [];
          // The one client-side record of a send from the wizard: "Finished Click" fires for a
          // bare Continue too, and POST /referral/invites tracks nothing. `created_count` is
          // what the API accepted; `skip_reasons` says why the rest weren't (self, already
          // invited, etc.).
          Mixpanel.track('Onboarding Wizard - Invites Sent', {
            ...inviteSendTrackingProps(unsent, response.data),
            is_math_offer: isMathFreeYearOffer,
          });
          // Cumulative across every attempt, straight from the API — `created` is only this
          // batch, so inviting 2 then 3 against a threshold of 5 would read "3 of 5" and
          // never unlock. Null once the ladder is fully earned: nothing left to ask for.
          const progress = response.data.progress || null;
          setInviteOutcomes((prev) => mergeInviteOutcomes(prev, response.data));
          // Not under the math offer: `progress` describes the invited-step program, which it
          // doesn't ask against. Its ask is "send a free year", with no count to reach.
          if (!isMathFreeYearOffer) setInviteProgress(progress);

          const reward = rewards[0];
          // A reward coming back means the threshold was crossed on this submit, so check it
          // before any shortfall nudge — otherwise a successful unlock gets held on the step.
          if (!reward && !isMathFreeYearOffer) {
            const remaining = progress?.remaining ?? 0;
            if (remaining > 0) {
              // Nudge, not a gate — hold on the step and let the shortfall message ask for
              // the rest.
              staysOnCurrentStep = true;
              break;
            }
          }

          if (reward && isClaimableReward(reward)) {
            // Guard against double-adding: if selections.reward was already set
            // (the teacher went Back to invite_others and resubmitted), the
            // top-of-function rebuild above already included CLAIM_STEP once.
            if (!newSteps.some((s) => s.type === 'claim_reward')) {
              newSteps.push(CLAIM_STEP);
            }
            setStepOptions(newSteps);
            setSelections((prev) => ({ ...prev, reward }));
          }
          // else: no usable reward to claim — invite_others simply becomes the
          // final step below rather than advancing to a broken claim screen.
        } catch (err) {
          setInviteSubmitError(
            err.response?.data?.message ||
              err.message ||
              'Something went wrong sending invites. Please try again.'
          );
          setHasInviteSendFailed(true);
          staysOnCurrentStep = true;
        } finally {
          setIsInviteSubmitting(false);
        }
        break;
      }
    }

    if (staysOnCurrentStep) {
      return;
    }

    // Recompute both the index and the length together against the final newSteps
    // (which the switch above may have just appended CLAIM_STEP to) — reusing the
    // pre-switch index here would work today only because pushes always append,
    // which is fragile to rely on.
    currentStepIndex = newSteps.findIndex((step) => step.type === currentStep.type);
    isFinalStep = currentStepIndex === newSteps.length - 1;

    if (isFinalStep) {
      // False when an X or "Not now" pressed mid-send got there first.
      if (!(await closeWizard())) return;
      Mixpanel.track('Onboarding Wizard - Finished Click');
      showAvatarMenuTip();
      return;
    }
    const nextStep = newSteps[currentStepIndex + 1];
    Mixpanel.track('Onboarding Wizard - Continue Click', {
      current_step: currentStep.type,
      next_step: nextStep.type,
    });
    setCurrentStep(nextStep);
  };

  // Only a 'template' reward renders its own button (ReferralRewardDetail); the other
  // reward types are a line of text, so on those the wizard's own button stays primary.
  const claimStepHasOwnCta =
    currentStep.type === 'claim_reward' && selections.reward?.reward_type === 'template';

  // The invite step's only exit for a genuine first-time user: isExitable (below)
  // is false until hasCompletedOnboardingBts2026 is set, which no longer happens
  // until this wizard closes — so without this, they can't back out of inviting.
  // No save needed: did_complete_onboarding was already set unconditionally at the
  // school step, and skips both the invite and claim steps without posting.
  const onClickNotNow = async () => {
    Mixpanel.track('Onboarding Wizard - Invite Not Now Click');
    if (await closeWizard()) showAvatarMenuTip();
  };

  // Applied here rather than read off `currentStep`: that object was built one Continue click
  // earlier. `currentStep` itself is left alone — stepOptions.indexOf below matches by identity.
  const isMathInviteStep = currentStep.type === 'invite_others' && isMathFreeYearOffer;
  // Whether the click will send — the chips plus a valid uncommitted draft, the same set
  // onClickContinue submits — so the button says what it is about to do.
  const hasInviteAddresses =
    selections.invited.length > 0 || splitAddressInput(selections.inviteDraft).some(isValidEmail);
  // The one step whose content has no natural ceiling — a teacher can keep adding chips until it
  // outgrows the viewport. Only there does the panel become a bounded column with a scrolling
  // middle; every other step keeps overflow-visible, which the school step's results dropdown
  // needs to escape the panel.
  const isInviteStep = currentStep.type === 'invite_others';
  // The same /r/<code> ShareReferralModal hands out. ReferralRedirect forwards the query, so
  // ref_src survives to the capture.
  const shareLink = referralCode
    ? `${window.location.origin}/r/${referralCode}?ref_src=${LINK_SOURCE.COPY_LINK_ONBOARDING}`
    : null;
  const onCopyLink = () => {
    Mixpanel.track('Onboarding Wizard - Link Copied', { is_math_offer: isMathInviteStep });
  };
  const mathStepCopy = freeYearError ? MATH_STEP_REFUSED_COPY : MATH_STEP_COPY;
  const displayedStepTitle = isMathInviteStep ? mathStepCopy.title : currentStep.title;

  return (
    <BasicModal
      showModal={showWizard}
      setShowModal={async () => {
        if (await closeWizard()) Mixpanel.track('Onboarding Wizard - Closed');
      }}
      isExitable={hasCompletedOnboardingBts2026 !== undefined}
      dialogPadding='p-8 sm:p-11'
      overflowY={isInviteStep ? 'overflow-hidden' : 'overflow-visible'}
      flexColumn={isInviteStep}
      maxWidth='sm:max-w-[386px]'
      icon={null}
      initialFocusRef={initialFocusRef}
      iconBackground={'bg-none'}>
      <Header
        currentStep={currentStep}
        currentStepIndex={stepOptions.findIndex((step) => step.type === currentStep.type)}
        totalSteps={stepOptions.length}
        onBack={() => {
          const currentStepIndex = stepOptions.findIndex((step) => step.type === currentStep.type);
          const previousStep = stepOptions[currentStepIndex - 1];
          Mixpanel.track('Onboarding Wizard - Back Click', {
            current_step: currentStep.type,
            back_to_step: previousStep.type,
          });
          setCurrentStep(previousStep);

          // always set this to false so it gets reset when they go back
          setIsAddSchoolFormShown(false);
        }}
        onClickRemindLater={async () => {
          Mixpanel.track('Onboarding Wizard - Remind Later Click');
          await saveUserSettings({
            did_click_remind_later_reonboarding: true,
          });
          await closeWizard();
        }}
      />
      <div className={`flex flex-col gap-y-8 mt-6 ${isInviteStep ? 'min-h-0 flex-1' : ''}`}>
        <StepTitle
          optional={isMathInviteStep ? MATH_STEP_COPY.optional : currentStep.optional}
          title={
            !selections.roles.includes('classroom_teacher')
              ? displayedStepTitle.replace('teaching', 'supporting')
              : displayedStepTitle
          }
          subheading={isMathInviteStep ? mathStepCopy.subheading : currentStep.subheading}
        />
        <div className={isInviteStep ? 'min-h-0 flex-1 overflow-y-auto' : undefined}>
          <StepContent
            initialFocusRef={initialFocusRef}
            currentStep={currentStep}
            selections={selections}
            setSelections={setSelections}
            setStepOptions={setStepOptions}
            setIsAddSchoolFormShown={setIsAddSchoolFormShown}
            isAddSchoolFormShown={isAddSchoolFormShown}
            inviteThreshold={inviteThreshold}
            hasBonusProgram={hasBonusProgram}
            bonusFootnote={bonusFootnote}
            isInviteSubmitting={isInviteSubmitting}
            inviteOutcomes={inviteOutcomes}
            inviteProgress={inviteProgress}
            inviteSubmitError={inviteSubmitError}
            isMathOffer={isMathInviteStep}
            freeYearError={freeYearError}
          />
        </div>
        {/* Continue + Copy my link stack 12px apart, as Send + Copy link do in ShareReferralModal.
            Gated like ContinueButton: an empty wrapper would still add a gap-y-8 under the
            add-school form. */}
        {!isAddSchoolFormShown && (
          <div className='flex flex-col gap-y-3'>
            <ContinueButton
              onClick={onClickContinue}
              disabled={isContinueDisabled()}
              totalSteps={stepOptions.length}
              stepIndex={stepOptions.indexOf(currentStep)}
              isAddSchoolFormShown={isAddSchoolFormShown}
              isSubmitting={isInviteSubmitting}
              isSecondary={claimStepHasOwnCta}
              // The math step's button is the ask's: it names the send once there is an address to
              // send to, and is otherwise the way out. Every other step keeps the position label.
              label={
                isClosing
                  ? // Any step: a returning teacher can go Back from the math step and press X there.
                    'One moment…'
                  : isMathInviteStep
                    ? isInviteSubmitting
                      ? 'Sending…'
                      : hasInviteAddresses
                        ? 'Send & Continue to Diffit'
                        : 'Continue to Diffit'
                    : undefined
              }
            />
            {/* Every signed-in teacher has a code; none means a stale payload, so no button. */}
            {isInviteStep && shareLink && (
              <CopyReferralLinkButton shareLink={shareLink} onCopied={onCopyLink} />
            )}
          </div>
        )}
        {/* Under the math offer only once a send has failed and held the step: a first-time user
            has no X or Escape (isExitable), and the button now retries the send. Otherwise
            "Continue to Diffit" is the exit. Sticky, so it doesn't blink out during the retry. */}
        {currentStep.type === 'invite_others' &&
          (!isMathInviteStep || hasInviteSendFailed) &&
          !isAddSchoolFormShown && (
            <button
              type='button'
              onClick={onClickNotNow}
              className='w-full text-center text-sm text-gray-500 hover:text-gray-700'>
              <span>Not now</span>
            </button>
          )}
      </div>
    </BasicModal>
  );
};

const StepContent = ({
  initialFocusRef,
  currentStep,
  selections,
  setSelections,
  isAddSchoolFormShown,
  setIsAddSchoolFormShown,
  inviteThreshold,
  hasBonusProgram,
  bonusFootnote,
  isInviteSubmitting,
  inviteOutcomes,
  inviteProgress,
  inviteSubmitError,
  isMathOffer,
  freeYearError,
}) => {
  return (
    <>
      {currentStep.type === 'role' && (
        <RoleSelection
          selections={selections}
          setSelections={setSelections}
          initialFocusRef={initialFocusRef}
        />
      )}
      {currentStep.type === 'intervention_specialist_type' && (
        <InterventionSpecialistTypeSelection
          selections={selections}
          setSelections={setSelections}
        />
      )}
      {currentStep.type === 'grade' && (
        <GradeSelection selections={selections} setSelections={setSelections} />
      )}
      {currentStep.type === 'subject' && (
        <SubjectSelection selections={selections} setSelections={setSelections} />
      )}
      {currentStep.type === 'classes' && (
        <ClassesSelection selections={selections} setSelections={setSelections} />
      )}
      {currentStep.type === 'school' && (
        <SchoolSelection
          selections={selections}
          setSelections={setSelections}
          isAddSchoolFormShown={isAddSchoolFormShown}
          setIsAddSchoolFormShown={setIsAddSchoolFormShown}
        />
      )}
      {currentStep.type === 'all_set' && (
        <AllSet selections={selections} setSelections={setSelections} />
      )}
      {/* A refused grant, above the ask — which stands regardless. */}
      {isMathOffer && freeYearError && (
        <p className='text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2'>
          <span>{freeYearError}</span>
        </p>
      )}
      {currentStep.type === 'invite_others' && (
        <>
          <InviteOthersForm
            selections={selections}
            setSelections={setSelections}
            // No counter or shortfall nudge under the math offer — they would state terms nobody
            // was given. Matches ShareReferralModal.
            threshold={isMathOffer ? undefined : inviteThreshold}
            programKey={isMathOffer ? MATH_REFERRAL_PROGRAM_KEY : INVITE_PROGRAM_KEY}
            isSubmitting={isInviteSubmitting}
            outcomes={inviteOutcomes}
            progress={isMathOffer ? null : inviteProgress}
            submitError={inviteSubmitError}
          />
          {hasBonusProgram && <ReferralBonusFootnote text={bonusFootnote} />}
        </>
      )}
      {currentStep.type === 'claim_reward' && <ReferralRewardDetail reward={selections.reward} />}
    </>
  );
};

const Header = ({ currentStep, currentStepIndex, totalSteps, onBack, onClickRemindLater }) => {
  const { diffitUser } = useDiffitUserContext();
  const {
    user_completed_onboarding: completedLegacyOnboarding,
    has_completed_onboarding_bts_2025: completedBts2025,
    has_completed_onboarding_bts_2026: hasCompletedOnboardingBts2026,
  } = diffitUser.seen_nux_json || {};
  const hasCompletedOldOnboarding = completedLegacyOnboarding || completedBts2025 === true;

  // if on last step, return nul
  if (currentStep.type === 'all_set') {
    return null;
  }

  const isReturningUser = hasCompletedOldOnboarding && hasCompletedOnboardingBts2026 !== true;

  if (currentStepIndex === 0) {
    // if they've completed the wizard before, don't show the header
    if (hasCompletedOnboardingBts2026) {
      return null;
    }

    return (
      <div className='flex flex-col gap-y-1 text-sm bg-gray-50 px-4 py-3 rounded-md'>
        <p>
          {' '}
          <span className='font-bold inline me-0.5'>
            Welcome{isReturningUser ? ' Back' : ''}! 👋
          </span>
          <span className='text-gray-600 inline'>
            We've got a few quick questions to align Diffit to your needs.
          </span>
        </p>
        {isReturningUser && (
          <button
            onClick={onClickRemindLater}
            className='text-emerald-600 text-left hover:text-emerald-500 text-sm flex items-center gap-x-0 w-fit rounded-md'>
            Remind me later
            <ChevronRightIcon className='w-4 h-4' />
          </button>
        )}
      </div>
    );
  }
  return (
    <div
      className={`flex justify-between items-center absolute top-2 left-3 sm:left-5 right-2 sm:right-5 px-3  pt-1.5 sm:pt-2 pointer-events-none`}>
      <button
        aria-label='Back to previous step'
        onClick={onBack}
        className='text-gray-400 hover:text-gray-500 pointer-events-auto'>
        <ChevronLeftIcon className='w-7 h-7' />
      </button>
      <div className='flex gap-x-2 items-center'>
        {Array.from({ length: totalSteps }).map((_, index) => (
          <div
            key={index}
            className={`w-2 h-2 rounded-full ${index <= currentStepIndex ? 'bg-emerald-500' : 'bg-gray-200'}`}
          />
        ))}
      </div>
      {/* empty div for spacing */}
      <div className='w-6 h-6'></div>
    </div>
  );
};

const ContinueButton = ({
  onClick,
  disabled,
  totalSteps,
  stepIndex,
  isAddSchoolFormShown,
  isSubmitting,
  isSecondary = false,
  // Overrides the step-position label for a button that doesn't advance the wizard.
  label,
}) => {
  if (isAddSchoolFormShown) {
    return null;
  }
  // Matches the invite step's "Not now" affordance, so a screen never shows two
  // emerald buttons competing to be the primary action.
  if (isSecondary) {
    return (
      <button
        type='button'
        disabled={disabled}
        onClick={onClick}
        className='w-full text-center text-sm text-gray-500 hover:text-gray-700'>
        <span>Get started with Diffit</span>
      </button>
    );
  }
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      className={`w-full px-3 py-2 font-semibold  text-white rounded-md ${
        disabled ? 'bg-gray-300' : 'hover:bg-emerald-500 bg-emerald-600'
      }`}>
      {label
        ? label
        : isSubmitting
          ? 'Sending…'
          : stepIndex === totalSteps - 1
            ? 'Get Started with Diffit!'
            : 'Continue'}
    </button>
  );
};

// The invite step's outlined copy-link CTA, styled like ShareReferralModal's. `onCopied` fires
// only after the clipboard write succeeds; a refused write is silent and the button stays ready.
// focus-visible, as on ReferralCodeCard, so a mouse click leaves no ring.
const CopyReferralLinkButton = ({ shareLink, onCopied }) => {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef(null);

  // The wizard may close before the 2s reset fires.
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
    } catch {
      return;
    }
    onCopied?.();
    setCopied(true);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <button
        type='button'
        onClick={handleCopy}
        className='inline-flex w-full items-center justify-center gap-1 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-600'>
        {copied ? (
          <>
            <CheckIcon className='h-4 w-4 text-emerald-600' />
            <span>Copied</span>
          </>
        ) : (
          <>
            <LinkIcon className='h-4 w-4' />
            <span>Copy my link</span>
          </>
        )}
      </button>
      {/* Announced for screen readers — the icon/label swap alone is a visual-only cue. */}
      <span aria-live='polite' className='sr-only'>
        {copied ? 'Link copied to clipboard' : ''}
      </span>
    </>
  );
};

const StepTitle = ({ title, subheading, optional }) => {
  return (
    <div>
      {/* An eyebrow above the title rather than a parenthetical after it — the title line
          reads as one thing, and the label needs no parentheses standing on its own. */}
      {optional && (
        <p className='text-sm font-normal italic text-gray-500'>
          <span>Optional</span>
        </p>
      )}
      <div className='text-2xl font-semibold leading-tight'>{title}</div>
      {subheading && <div className='text-sm text-gray-500'>{subheading}</div>}
    </div>
  );
};

const AllSet = () => {
  return (
    <div>
      <span>Packets</span>
    </div>
  );
};
